<template>
  <v-form ref="CONTRAT_TEMPLATE_CONFIG" @submit.prevent="sectionSave" lazy-validation class="section">
    <div>
      <section-header v-if="!hideTitle"
                      :ressource="ressource"
                      :isExtended="extended"
                      :dialog="dialog"
                      :picto="picto()"
                      :fiche=fiche
                      :isAbstract=isAbstract
                      @extended=setExtended
                      @closeDialog=closeDialog
                      ref="CONTRAT_TEMPLATE_CONFIG">
      </section-header>
      <div v-if="extended" id="field-container" :class="dialog ? 'p-0 p-md-3' : 'py-2'">
        <contrat-template-name v-if="isDisplayed('NAME')"
                               ref="NAME"
                               :theme="theme"
                               :showMissing="showMissing"
                               v-model:value=name
                               @status=statusName
                               :over-ride-placeholder=placeholderName
                               :required="isRequired('NAME')"
                               :contextId=contratTemplate.id
                               :readOnly="readOnly('NAME')"
                               :objType=objType
                               :fiche=fiche
                               :sectionName="sectionName">
        </contrat-template-name>
        <contrat-template-titre v-if="isDisplayed('TITRE')"
                                ref="TITRE"
                                :theme="theme"
                                :showMissing="showMissing"
                                v-model:value=titre
                                @status=statusTitre
                                :over-ride-placeholder=placeholderTitre
                                :required="isRequired('TITRE')"
                                :contextId=contratTemplate.id
                                :readOnly="readOnly('TITRE')"
                                :objType=objType
                                :fiche=fiche
                                :sectionName="sectionName">
        </contrat-template-titre>
        <etablissement-select v-if="isDisplayed('ETABLISSEMENT_ID')"
                              ref="ETABLISSEMENT_ID"
                              :theme="theme"
                              :showMissing="showMissing"
                              v-model:value=etablissementId
                              :moduleId=moduleId
                              libelle="'Établissement'"
                              newLabel=''
                              selectText=""
                              @status=statusEtablissementId
                              :requiredAttributs="requiredAttributs"
                              :autoSelect=true
                              :ressource=ressourceEtablissementSelect
                              :filters=etablissementIdFilter
                              :toggleReset=resetEtablissementId
                              :required="isRequired('ETABLISSEMENT_ID')"
                              :contextId=contratTemplate.id
                              :readOnly="readOnly('ETABLISSEMENT_ID')"
                              :objType=objType
                              :fiche=fiche
                              :sectionName="sectionName">
        </etablissement-select>
        <contrat-template-use-convention v-if="isDisplayed('USE_CONVENTION')"
                                         ref="USE_CONVENTION"
                                         :theme="theme"
                                         :showMissing="showMissing"
                                         v-model:value=useConvention
                                         @status=statusUseConvention
                                         :required="isRequired('USE_CONVENTION')"
                                         :contextId=contratTemplate.id
                                         :readOnly="readOnly('USE_CONVENTION')"
                                         :objType=objType
                                         :fiche=fiche
                                         :sectionName="sectionName">
        </contrat-template-use-convention>
        <contrat-template-active v-if="isDisplayed('ACTIVE')"
                                 ref="ACTIVE"
                                 :theme="theme"
                                 :showMissing="showMissing"
                                 v-model:value=active
                                 @status=statusActive
                                 :required="isRequired('ACTIVE')"
                                 :contextId=contratTemplate.id
                                 :readOnly="readOnly('ACTIVE')"
                                 :objType=objType
                                 :fiche=fiche
                                 :sectionName="sectionName">
        </contrat-template-active>
      </div>
    </div>
    <div v-if="extended" class="d-flex flex-row-reverse">
      <loader-button v-if="saveButton"
                     class="ms-2"
                     type="submit"
                     :loading="saveLoading"
                     :label="saveLabel"
                     :disabled="!updatedAndValid">
      </loader-button>
      <div v-for="(button, index) in extraButtons" :key="index">
        <loader-button class="ms-2"
                       :outlined="button.outlined"
                       @callback="button.callback()"
                       :type="button.type"
                       :label="button.label"
                       :loading="button.loading"
                       :disabled="button.disabled">
        </loader-button>
      </div>
    </div>
    <div class="d-flex flex-row-reverse" v-if="saveFailed && saveButton && extended">
      <p class="text-12 text-bold error-text">{{errorTxt}}</p>
    </div>
  </v-form>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import CrudContratTemplate from '@/generated/cruds/CrudContratTemplate';
  // eslint-disable-next-line
  import { GenControl, GenSection, RessourceGenControl, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import { FwkButtons, RessourceFwkButton, RessourceFwkSection, FwkSections } from '@/ressources/globalRessources';
  import { Sections } from '@/generated/classes/AppSchema';
  import Button, { ButtonCallback } from '@/framework/base/classes/Button';
  import { ColumnFilter, ListChoix } from '@/extend/classes/ListData';
  import { ObjTypes } from '@/generated/classes/ObjTypes';

  export default defineComponent({
    extends:  CrudContratTemplate,
    components: {},
    props: {
      hideTitle: {
        type: Boolean,
      },
      isAbstract: {
        type: Boolean,
      },
    },

    data() {
      return {
        sectionName: Sections.CONTRAT_TEMPLATE_CONFIG,
        displayMap: new Map<string, Boolean>,
        readOnlyMap: new Map<string, Boolean>,
        requiredMap: new Map<string, Boolean>,
        placeholderName: null,
        placeholderTitre: null,
      }
    },

    computed: {
      saveLabel () { 
        return RessourceFwkButton(FwkButtons.ENREGISTRER).label;
      },

      errorTxt () { 
        return RessourceFwkSection(FwkSections.ERROR_TEXT).label;
      },

      ressourceEtablissementSelect () { return RessourceGenControl(GenControl.CONTRAT_TEMPLATE_CONFIG_ETABLISSEMENT_SELECT); },

      ressource () { return RessourceGenSection(GenSection.CONTRAT_TEMPLATE_CONFIG); },

      fields (): string[] {
        return ['NAME', 'TITRE', 'ETABLISSEMENT_ID', 'USE_CONVENTION', 'ACTIVE'];
      },

      extraButtons (): Button[] {
        return [];
      },

      etablissementIdFilter (): ColumnFilter[] {
        const res: ColumnFilter[] = [];
        return res;
      },
    },
    methods: {
      getRequiredAttributs(): string[] {
        const res: string[] = [];
        if (this.requiredAttributs) {
          this.requiredAttributs.forEach((attrib) => {
            const value = attrib.split(".");
            if (value[0] == this.sectionName) {
              res.push(value[1])
            }
          })
        }
        return res;
      },

      requiredMissing(requiredAttributs: string[], value: string): boolean {
        return requiredAttributs.length > 0 && requiredAttributs.includes(value);;
      },

      readOnly (value: string): boolean {
        let res = false;
        if (!this.editContratTemplate.canUpdate) {
          return true;
        }
        if (this.readOnlyMap.size == 0) {
          this.readOnlyMap.set("NAME", false);
          this.readOnlyMap.set("TITRE", false);
          this.readOnlyMap.set("ETABLISSEMENT_ID", false);
          this.readOnlyMap.set("USE_CONVENTION", false);
          this.readOnlyMap.set("ACTIVE", false);
        }
        const readOnly = this.readOnlyMap.get(value);
        if (readOnly != undefined) {
          res = readOnly;
        }
        return res;
      },

      isDisplayed (value: string): boolean {
        let res = true;
        if (this.displayMap.size == 0) {
        const requiredAttributs = this.getRequiredAttributs();
          this.displayMap.set("NAME", true);
          this.displayMap.set("TITRE", true);
          this.displayMap.set("ETABLISSEMENT_ID", true);
          this.displayMap.set("USE_CONVENTION", this.requiredMissing(requiredAttributs, "USE_CONVENTION") || !!this.fiche);
          this.displayMap.set("ACTIVE", this.requiredMissing(requiredAttributs, "ACTIVE") || !!this.fiche);
        }
        const display = this.displayMap.get(value);
        if (display != undefined) {
          res = display;
        }
        return res;
      },

      isRequired (value: string): boolean {
        let res = true;
        const requiredAttributs = this.getRequiredAttributs();
        if (this.requiredMap.size == 0) {
          this.requiredMap.set("NAME", true);
          this.requiredMap.set("TITRE", true);
          this.requiredMap.set("ETABLISSEMENT_ID", true);
          this.requiredMap.set("USE_CONVENTION", this.requiredMissing(requiredAttributs, "USE_CONVENTION") || !this.fiche);
          this.requiredMap.set("ACTIVE", this.requiredMissing(requiredAttributs, "ACTIVE") || !this.fiche);
        }
        const required = this.requiredMap.get(value);
        if (required != undefined) {
          res = required;
        }
        return res;
      },

      picto (): string {
        return 'SectionPictoConfig' 
      },

      sectionSave (): void {
        return this.save();
      }
    }
  })
</script>
